/**
 * The write-only key field both Saidiseire key panels share (PageSpeed and
 * Wordfence, `PagespeedCard.tsx` and `WordfenceCard.tsx`), and the one read
 * behind them (`GET /admin/crm/monitoring/keys`).
 *
 * The same rules as the Web Risk panel it was lifted from: the stored key never
 * comes back, so the box starts empty and an untouched box keeps it; emptying
 * the box and saving DELETES the key, and the hint, the placeholder and a
 * warning all say so at that moment rather than the opposite.
 *
 * Each panel saves only its own key: the PUT carries one field, and the other
 * key is left as it is.
 */
import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { getMonitorKeys, saveMonitorKeys, type MonitorKeysView } from "../../../../lib/monitoringKeys";
import { Btn } from "../../../ui";
import { Field, Note, SaveRow, SecretPill } from "../panel/fields";

/** The keys view, read once when the panel opens. `view` null while loading or when it failed. */
export function useMonitorKeys(): { view: MonitorKeysView | null; loading: boolean; setView: (v: MonitorKeysView) => void } {
  const [view, setView] = useState<MonitorKeysView | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    getMonitorKeys()
      .then(setView)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  return { view, loading, setView };
}

export function MonitorKeyForm({
  name,
  keySet,
  onSaved,
}: {
  /** Which key this panel owns; its copy lives under `settings.<name>`. */
  name: "pagespeed" | "wordfence";
  keySet: boolean;
  onSaved: (view: MonitorKeysView) => void;
}) {
  const t = useT();
  const p = `settings.${name}`;
  // Starts empty and stays empty: the stored key never comes back from the server.
  const [apiKey, setApiKey] = useState("");
  const [touched, setTouched] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const clearing = touched && !apiKey.trim() && keySet;

  const save = async () => {
    setSaveState("saving");
    try {
      const value = apiKey.trim();
      onSaved(await saveMonitorKeys(name === "pagespeed" ? { pagespeedApiKey: value } : { wordfenceApiKey: value }));
      setApiKey("");
      setTouched(false);
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  };

  return (
    <>
      <Field
        label={t(`${p}.apiKey`)}
        hint={clearing ? t(`${p}.clearWarning`) : keySet ? t(`${p}.apiKeyStored`) : t(`${p}.apiKeyMissing`)}
      >
        <SecretPill
          value={apiKey}
          onChange={(v) => {
            setApiKey(v);
            setTouched(true);
            setSaveState("idle");
          }}
          placeholder={clearing ? t(`${p}.apiKeyClears`) : keySet ? t(`${p}.apiKeyKeep`) : t(`${p}.apiKeyEmpty`)}
          reveal
          showLabel={t(`${p}.reveal`)}
          hideLabel={t(`${p}.hide`)}
        />
      </Field>

      {clearing ? <Note tone="warn">{t(`${p}.clearWarning`)}</Note> : null}

      <SaveRow>
        <Btn onClick={save} disabled={!touched || saveState === "saving"}>
          <Save className="size-4" /> {t("common.save")}
        </Btn>
        {saveState === "saved" ? <span className="crm-sub">{t("common.saved")}</span> : null}
        {saveState === "error" ? <span className="crm-sub">{t("common.error")}</span> : null}
      </SaveRow>
    </>
  );
}
